import type { JSONObject } from '@/type-utils';
import { InvalidArgumentError, MissingTokenError } from '../errors';
import { Time } from '../time-utils';

/**
 * Token properties required for expiration checking.
 * This is a subset of IUserTokens to avoid cross-library imports.
 */
export interface ITokenExpiration {
  /** Token expiration time in seconds. */
  expiresIn: number;
  /** Timestamp when tokens were issued (Unix timestamp in seconds). */
  issuedAt: number;
}

/** Token property names that can be validated. */
type ValidatableTokenKey =
  | 'accessToken'
  | 'refreshToken'
  | 'idToken'
  | 'guestId';

/**
 * Validates that required token properties are present and returns them.
 * Throws an InvalidArgumentError if any required tokens are missing.
 *
 * @param tokens - The tokens object to validate (IUserTokens or similar).
 * @param requiredTokens - Array of token property names that must be present.
 * @returns The validated tokens object with required properties guaranteed.
 * @throws {InvalidArgumentError} If any required tokens are missing or empty.
 *
 * @example
 * ```typescript
 * // Validate that accessToken is present
 * const { accessToken } = requireTokens(tokens, ['accessToken']);
 *
 * // Validate multiple tokens
 * const { accessToken, refreshToken } = requireTokens(tokens, ['accessToken', 'refreshToken']);
 * ```
 */
export const requireTokens = <
  T extends Partial<Record<ValidatableTokenKey, string | undefined>>,
  K extends ValidatableTokenKey
>(
  tokens: T,
  requiredTokens: Array<K>
): T & Required<Pick<T, K>> => {
  const missingTokens: Array<string> = [];

  for (const tokenName of requiredTokens) {
    const value = tokens[tokenName];
    if (typeof value !== 'string' || value.trim() === '') {
      missingTokens.push(String(tokenName));
    }
  }

  if (missingTokens.length > 0) {
    const tokenList = missingTokens.join(', ');
    throw new MissingTokenError(
      `Missing required token(s): ${tokenList}. ` +
        'Ensure the user is authenticated and has valid tokens.'
    );
  }

  return tokens as T & Required<Pick<T, K>>;
};

/**
 * Validates that token expiration values are valid numbers.
 * @param tokens - Token expiration data to validate.
 * @throws {InvalidArgumentError} If issuedAt or expiresIn is NaN.
 */
const validateTokenExpiration = (tokens: ITokenExpiration): void => {
  if (Number.isNaN(tokens.issuedAt)) {
    throw new InvalidArgumentError(
      'Invalid token: issuedAt is NaN. Token may be corrupted or missing.'
    );
  }
  if (Number.isNaN(tokens.expiresIn)) {
    throw new InvalidArgumentError(
      'Invalid token: expiresIn is NaN. Token may be corrupted or missing.'
    );
  }
};

/**
 * Checks if a token is expired based on its issuedAt and expiresIn values.
 * @param tokens - User tokens to check for expiration.
 * @returns True if tokens are expired, false otherwise.
 * @throws {InvalidArgumentError} If issuedAt or expiresIn is NaN.
 */
export const isTokenExpired = (tokens: ITokenExpiration): boolean => {
  validateTokenExpiration(tokens);
  const expiresAt = tokens.issuedAt + tokens.expiresIn;
  return Time.nowInSeconds() > expiresAt;
};

/**
 * Calculates when a token will expire.
 * @param tokens - User tokens.
 * @returns Unix timestamp when the token expires.
 * @throws {InvalidArgumentError} If issuedAt or expiresIn is NaN.
 */
export const getTokenExpirationTime = (tokens: ITokenExpiration): number => {
  validateTokenExpiration(tokens);
  return tokens.issuedAt + tokens.expiresIn;
};

/**
 * Checks if a token will expire within a given number of seconds.
 * Useful for proactive token refresh.
 * @param tokens - User tokens to check.
 * @param bufferSeconds - Number of seconds before expiration to consider expired.
 * @returns True if token expires within the buffer time.
 */
export const willTokenExpireSoon = (
  tokens: ITokenExpiration,
  bufferSeconds: number = 300
): boolean => {
  const expiresAt = getTokenExpirationTime(tokens);
  const now = Time.nowInSeconds();

  return now + bufferSeconds >= expiresAt;
};

/**
 * Checks if a token needs to be refreshed.
 * Returns true if the token is invalid (NaN values) or will expire soon.
 *
 * @param tokens - Token expiration data to check.
 * @param bufferSeconds - Number of seconds before expiration to trigger refresh.
 * @returns True if token needs refresh, false otherwise.
 * @throws Re-throws unexpected errors (non-InvalidArgumentError).
 */
export const tokenNeedsRefresh = (
  tokens: ITokenExpiration,
  bufferSeconds: number = 300
): boolean => {
  try {
    return willTokenExpireSoon(tokens, bufferSeconds);
  } catch (error) {
    if (error instanceof InvalidArgumentError) {
      // Invalid token data (NaN) - needs refresh to get valid tokens
      return true;
    }
    throw error;
  }
};

/**
 * Extracts and decodes the payload from a JSON Web Token.
 * Returns null if the token is invalid or cannot be decoded.
 *
 * @see [Introduction to JSON Web Tokens](https://jwt.io/introduction) for more on JWT Structure.
 *
 * @param token - The encoded JWT.
 * @returns The decoded payload, or null if invalid.
 */
export const tryDecodeJWTPayload = <T = Record<string, unknown>>(
  token: string
): T | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = Buffer.from(parts[1], 'base64').toString('utf-8');
    return JSON.parse(payload) as T;
  } catch {
    return null;
  }
};

/**
 * Extracts and decodes the payload from a JSON Web Token.
 * Throws if the token is invalid or cannot be decoded.
 *
 * @see [Introduction to JSON Web Tokens](https://jwt.io/introduction) for more on JWT Structure.
 *
 * @param token - The encoded JWT.
 * @returns The decoded payload, typed to the specified generic type.
 * @throws Error if token format is invalid or payload cannot be parsed.
 */
export const decodeJWTPayload = <T = JSONObject<true>>(token: string): T => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format: token must have 3 parts');
  }

  const payload = Buffer.from(parts[1], 'base64').toString('utf-8');
  return JSON.parse(payload) as T;
};

/**
 * Extracts the `iat` (issued at) claim from a JWT access token.
 *
 * This is safe to use when the token is received directly from the
 * authorization server over TLS, as we trust the token source.
 *
 * @param accessToken - The JWT access token.
 * @returns The `iat` timestamp (Unix seconds).
 * @throws {InvalidArgumentError} If the token cannot be decoded or `iat` claim
 * is missing.
 */
export const extractJWTIssuedAt = (accessToken: string): number => {
  const payload = tryDecodeJWTPayload<{ iat?: number }>(accessToken);

  if (!payload) {
    throw new InvalidArgumentError(
      'Failed to decode JWT: invalid token format'
    );
  }

  if (typeof payload.iat !== 'number') {
    throw new InvalidArgumentError(
      'JWT missing required "iat" (issued at) claim'
    );
  }

  return payload.iat;
};
